/* Native scrolling owns the gesture. This layer only adds depth and accessible controls. */
(() => {
  'use strict';
  if (window.CeikaCardRails) {
    window.CeikaCardRails.mount(document);
    return;
  }

  const instances = new Map();
  const mobile = window.matchMedia('(max-width: 600px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

  function mountRail(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('[data-family-grid], .cvh-grid');
    const slides = Array.from(track?.querySelectorAll(':scope > .cvh-card-slide') || []);
    const cards = slides.map(slide => slide.querySelector('.cvh-card'));
    if (!track || slides.length < 2 || cards.some(card => !card)) return;

    const controls = root.querySelector('[data-rail-controls]');
    const previous = root.querySelector('[data-rail-prev]');
    const next = root.querySelector('[data-rail-next]');
    const current = root.querySelector('[data-rail-current]');
    const total = root.querySelector('[data-rail-total]');
    const status = root.querySelector('[data-rail-status]');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const trackAttributes = ['role', 'aria-roledescription', 'aria-label', 'tabindex'];
    const savedAttributes = new Map(trackAttributes.map(name => [name, track.getAttribute(name)]));
    let offsets = [], step = 1, activeIndex = Math.max(0, cards.findIndex(card => card.dataset.family === root.dataset.railStartFamily)), frame = 0, settleTimer = 0;
    let touching = false, touchX = 0, touchY = 0, pointerId = null;
    let enabled = false, destroyed = false, lastAnnouncement = '';
    let revealTimer = 0, pendingReveal = false;
    const hub = root.closest('[data-ceika-vehicle-hub]');

    function viewportBounds() {
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const height = viewport?.height || window.innerHeight;
      let inset = 0;
      document.querySelectorAll('#header, .sticky_nav').forEach(header => {
        const style = getComputedStyle(header), rect = header.getBoundingClientRect();
        if (['fixed', 'sticky'].includes(style.position) && rect.height > 0 && rect.top <= top + 2 && rect.bottom > top) inset = Math.max(inset, rect.bottom - top);
      });
      const preview = document.getElementById('PBarNextFrame');
      const previewInset = preview && getComputedStyle(preview).display !== 'none' ? 64 : 0;
      return { top: top + inset, bottom: top + height - previewInset, height: Math.max(200, height - inset - previewInset) };
    }

    function fitViewport() {
      if (!enabled || destroyed) return;
      const bounds = viewportBounds();
      const controlsHeight = controls && !controls.hidden ? controls.getBoundingClientRect().height : 0;
      const budget = Math.floor(Math.max(220, bounds.height - controlsHeight - 60));
      root.dataset.cardDensity = budget < 450 ? 'compact' : 'comfortable';
      const value = budget + 'px';
      if (root.style.getPropertyValue('--cvh-card-budget') !== value) root.style.setProperty('--cvh-card-budget', value);
    }

    function cancelReveal() { pendingReveal = false; clearTimeout(revealTimer); }

    function queueReveal() {
      if (!pendingReveal || !enabled || destroyed) return;
      clearTimeout(revealTimer);
      revealTimer = setTimeout(() => {
        if (!pendingReveal || !enabled || destroyed) return;
        pendingReveal = false;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!enabled || destroyed) return;
          fitViewport();
          const bounds = viewportBounds(), rail = root.getBoundingClientRect();
          const card = cards[activeIndex], action = card.querySelector('.cvh-button');
          let delta = rail.top + rail.height / 2 - (bounds.top + bounds.bottom) / 2;
          if (action) delta = Math.max(delta, action.getBoundingClientRect().bottom - bounds.bottom + 12);
          window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        }));
      }, 180);
    }

    function resizeViewport() { fitViewport(); if (pendingReveal) queueReveal(); }

    function itemLabel(index) {
      const title = cards[index].querySelector('h2, h3')?.textContent.trim() || '';
      const template = root.dataset.railItemLabel || '{{ current }} of {{ total }}: {{ title }}';
      const values = { current: index + 1, total: slides.length, title };
      return template.replace(/\{\{\s*(current|total|title)\s*\}\}|%(current|total|title)%/g, (_, liquidKey, tokenKey) => values[liquidKey || tokenKey]);
    }

    function updateControls(announce) {
      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      if (total) total.textContent = String(slides.length).padStart(2, '0');
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === slides.length - 1;
      cards.forEach((card, index) => { card.dataset.railActive = String(index === activeIndex); });
      if (announce && status) {
        const label = itemLabel(activeIndex);
        if (label !== lastAnnouncement) { status.textContent = label; lastAnnouncement = label; }
      }
    }

    function paint() {
      frame = 0;
      if (!enabled || destroyed || !offsets.length) return;
      const position = clamp(track.scrollLeft, 0, offsets[offsets.length - 1]);
      const nearest = offsets.reduce((best, offset, index) => Math.abs(offset - position) < Math.abs(offsets[best] - position) ? index : best, 0);
      if (nearest !== activeIndex) { activeIndex = nearest; updateControls(false); }
      slides.forEach((slide, index) => {
        const distance = (offsets[index] - position) / step;
        const depth = reducedMotion.matches ? 0 : Math.min(Math.abs(distance), 1);
        const card = cards[index];
        card.style.setProperty('--cvh-rail-x', `${(distance < 0 ? 38 : -52) * depth}px`);
        card.style.setProperty('--cvh-rail-y', `${8 * depth}px`);
        card.style.setProperty('--cvh-rail-scale', String(1 - .05 * depth));
        card.style.setProperty('--cvh-rail-photo-opacity', String(1 - .42 * depth));
        card.style.setProperty('--cvh-rail-content-opacity', String(1 - .35 * depth));
        card.style.setProperty('--cvh-rail-shadow', String(.065 - .04 * depth));
        slide.style.zIndex = String(10 - Math.round(Math.min(Math.abs(distance), 3) * 3));
      });
    }

    function queuePaint() {
      if (!frame && enabled) frame = requestAnimationFrame(paint);
    }

    function measure() {
      if (!enabled || destroyed) return;
      fitViewport();
      const first = slides[0].offsetLeft;
      offsets = slides.map(slide => slide.offsetLeft - first);
      step = offsets[1] - offsets[0] || 1;
      queuePaint();
    }

    function settle() {
      clearTimeout(settleTimer);
      if (!enabled || touching || destroyed) return;
      delete root.dataset.railMoving;
      cancelAnimationFrame(frame); frame = 0;
      paint();
      updateControls(true);
    }

    function scrollToIndex(index, instant = false) {
      if (!enabled || !offsets.length) return;
      const destination = clamp(index, 0, slides.length - 1);
      track.scrollTo({ left: offsets[destination], behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
      // Includes an already-selected card, where the browser need not dispatch scrollend.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, instant || reducedMotion.matches ? 80 : 180);
    }

    root.ceikaPrioritizeFamily = family => {
      const target=cards.findIndex(card=>card.dataset.family===family);
      if(target>=0){activeIndex=target;measure();scrollToIndex(target,true);updateControls(false);}
    };

    function endTouch(event) {
      if (event && event.pointerId !== pointerId) return;
      touching = false;
      pointerId = null;
      delete root.dataset.railTouching;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 160);
    }

    function applyMode() {
      enabled = mobile.matches;
      if (controls) controls.hidden = !enabled;
      root.dataset.railReady = String(enabled);
      if (enabled) {
        track.setAttribute('role', 'region');
        track.setAttribute('aria-label', root.dataset.railLabel || 'Product ranges');
        track.setAttribute('tabindex', '0');
        slides.forEach((slide, index) => {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-label', itemLabel(index));
        });
        measure();
        scrollToIndex(activeIndex, true);
        updateControls(false);
      } else {
        endTouch();
        clearTimeout(settleTimer);
        cancelAnimationFrame(frame); frame = 0;
        delete root.dataset.railMoving;
        savedAttributes.forEach((value, name) => value === null ? track.removeAttribute(name) : track.setAttribute(name, value));
        slides.forEach((slide, index) => {
          slide.removeAttribute('role'); slide.removeAttribute('aria-label'); slide.style.removeProperty('z-index');
          ['x', 'y', 'scale', 'photo-opacity', 'content-opacity', 'shadow'].forEach(name => cards[index].style.removeProperty(`--cvh-rail-${name}`));
          delete cards[index].dataset.railActive;
        });
        cancelReveal();
        root.style.removeProperty('--cvh-card-budget');
        delete root.dataset.cardDensity;
        if (status) status.textContent = '';
        lastAnnouncement = '';
      }
    }

    track.addEventListener('scroll', () => {
      if (!enabled) return;
      root.dataset.railMoving = 'true';
      queuePaint();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 160);
    }, { ...options, passive: true });
    track.addEventListener('scrollend', settle, options);
    track.addEventListener('pointerdown', event => {
      if (!enabled || !event.isPrimary || event.button !== 0) return;
      touching = true; pointerId = event.pointerId;
      touchX = event.clientX; touchY = event.clientY;
      root.dataset.railTouching = 'true';
      clearTimeout(settleTimer);
    }, { ...options, passive: true });
    track.addEventListener('pointermove', event => {
      if (!touching || event.pointerId !== pointerId) return;
      const dx = Math.abs(event.clientX - touchX), dy = Math.abs(event.clientY - touchY);
      if (dy > 8 && dy > dx) delete root.dataset.railTouching;
    }, { ...options, passive: true });
    window.addEventListener('pointerup', endTouch, { ...options, passive: true });
    window.addEventListener('pointercancel', endTouch, { ...options, passive: true });
    track.addEventListener('keydown', event => {
      if (!enabled || event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const target = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : activeIndex + (event.key === 'ArrowRight' ? 1 : -1);
      scrollToIndex(target);
    }, options);
    track.addEventListener('focusin', event => {
      if (!enabled || event.target === track) return;
      const index = slides.findIndex(slide => slide.contains(event.target));
      if (index >= 0) scrollToIndex(index, true);
    }, options);
    previous?.addEventListener('click', () => scrollToIndex(activeIndex - 1), options);
    next?.addEventListener('click', () => scrollToIndex(activeIndex + 1), options);
    hub?.addEventListener('ceika:vehicle-results-reveal', () => {
      if (!enabled) return;
      fitViewport(); pendingReveal = true; queueReveal();
    }, options);
    window.addEventListener('resize', resizeViewport, { ...options, passive: true });
    window.visualViewport?.addEventListener('resize', resizeViewport, { ...options, passive: true });
    window.addEventListener('wheel', cancelReveal, { ...options, passive: true });
    window.addEventListener('pointerdown', cancelReveal, { ...options, passive: true });
    mobile.addEventListener('change', applyMode, options);
    reducedMotion.addEventListener('change', queuePaint, options);
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);
    slides.forEach(slide => resizeObserver.observe(slide));
    applyMode();

    instances.set(root, () => {
      destroyed = true;
      delete root.ceikaPrioritizeFamily;
      controller.abort(); resizeObserver.disconnect();
      cancelReveal();
      clearTimeout(settleTimer); cancelAnimationFrame(frame);
      instances.delete(root);
    });
  }

  function mount(scope) {
    if (scope.matches?.('[data-card-rail]')) mountRail(scope);
    scope.querySelectorAll('[data-card-rail]').forEach(mountRail);
  }

  window.CeikaCardRails = { mount };
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => { if (event.target.contains(root)) destroy(); });
  });
  mount(document);
})();